<template>
  <div class="container-fluid mt-4" dir="rtl">
    
    <!-- 1. شريط التحكم والزمن -->
    <div class="d-flex justify-content-between align-items-center mb-4 no-print">
      <h4 class="fw-bold text-dark d-flex align-items-center">
        <i class="fas fa-satellite-dish text-danger ms-2"></i> المركز الرئيسي للعمليات
        <!-- علامة البث المباشر تظهر عند التحديث التلقائي -->
        <span v-if="autoRefresh" class="badge bg-danger ms-2 fade-animation d-flex align-items-center">
             <i class="fas fa-circle x-small me-1"></i> مباشر
        </span>
      </h4>
      
      <div class="d-flex gap-2 align-items-center">
        <!-- مؤشر تحديث صغير -->
        <span v-if="isRefreshing" class="text-muted small fw-bold">
            <i class="fas fa-sync fa-spin"></i> جاري جلب البيانات...
        </span>

        <button @click="toggleAutoRefresh" class="btn btn-sm shadow-sm transition-btn" :class="autoRefresh ? 'btn-success' : 'btn-outline-secondary'">
          <i class="fas" :class="autoRefresh ? 'fa-toggle-on' : 'fa-toggle-off'"></i> 
          {{ autoRefresh ? 'التحديث التلقائي: مفعل' : 'التحديث التلقائي: متوقف' }}
        </button>
        
        <button @click="manualRefresh" class="btn btn-dark btn-sm shadow-sm" :disabled="loading">
            <i class="fas fa-sync-alt"></i>
        </button>
      </div>
    </div>

    <!-- 2. شريط الحالة الحيوية (KPIs) -->
    <div class="row g-3 mb-4">
      <!-- البلاغات الواردة -->
      <div class="col-md-3">
        <div class="card border-0 shadow-sm h-100 border-end border-5 border-primary bg-white">
          <div class="card-body">
            <h6 class="text-muted small fw-bold">إجمالي البلاغات اليوم</h6>
            <div class="d-flex align-items-baseline">
              <h2 class="fw-bold mb-0 text-primary">{{ stats.total_today }}</h2>
              <span class="ms-2 text-muted small">بلاغ</span>
            </div>
          </div>
        </div>
      </div>

      <!-- البلاغات الخطرة -->
      <div class="col-md-3">
        <div class="card border-0 shadow-sm h-100 border-end border-5 border-danger bg-white">
          <div class="card-body">
            <h6 class="text-muted small fw-bold">حالات حرجة (أمني/عاجل)</h6>
            <div class="d-flex align-items-baseline">
              <h2 class="fw-bold mb-0 text-danger">{{ stats.urgent_count }}</h2>
              <span class="ms-2 badge bg-danger-subtle text-danger border border-danger-subtle" v-if="stats.urgent_count > 0">
                 تتطلب تدخل فوري
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- نسبة الإنجاز -->
      <div class="col-md-3">
        <div class="card border-0 shadow-sm h-100 border-end border-5 border-success bg-white">
          <div class="card-body">
            <h6 class="text-muted small fw-bold">نسبة حل المشكلات</h6>
            <div class="d-flex align-items-baseline">
              <h2 class="fw-bold mb-0 text-success">{{ stats.resolution_rate }}%</h2>
              <i class="fas fa-check-circle ms-auto text-success opacity-25 fa-2x"></i>
            </div>
          </div>
        </div>
      </div>

      <!-- الالتزام بالتعليمات -->
      <div class="col-md-3">
        <div class="card border-0 shadow-sm h-100 border-end border-5 border-warning bg-white">
          <div class="card-body">
            <h6 class="text-muted small fw-bold">نسبة قراءة التعليمات</h6>
            <div class="d-flex align-items-baseline">
              <h2 class="fw-bold mb-0 text-dark">{{ stats.read_rate }}%</h2>
              <span class="ms-2 text-muted small">من رؤساء اللجان</span>
            </div>
            <div class="progress mt-2" style="height: 5px;">
              <div class="progress-bar bg-warning" :style="{width: stats.read_rate + '%'}"></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 3. الرسوم البيانية والتحليل -->
    <div class="row g-4 mb-4">
      
      <!-- Chart 1: تصنيف البلاغات -->
      <div class="col-md-4">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-header bg-white fw-bold border-bottom-0 text-dark">تصنيف البلاغات</div>
          <div class="card-body position-relative d-flex justify-content-center align-items-center" style="height: 250px;">
            <Doughnut v-if="chartsData.types" :data="chartsData.types" :options="chartOptions.doughnut" />
          </div>
          <div class="card-footer bg-white text-center small text-muted border-0">
            الأكثر تكراراً: <strong class="text-dark">{{ stats.top_issue_type }}</strong>
          </div>
        </div>
      </div>

      <!-- Chart 2: موقف الحضور والغياب (Pie Chart) -->
      <div class="col-md-4">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-header bg-white fw-bold border-bottom-0 d-flex justify-content-between align-items-center">
             <span>نسب الحضور والغياب</span>
             <span class="badge bg-light text-dark border">الإجمالي</span>
          </div>
          <div class="card-body position-relative d-flex justify-content-center align-items-center" style="height: 250px;">
            <Pie v-if="chartsData.attendance" :data="chartsData.attendance" :options="chartOptions.pie" />
          </div>
          <div class="card-footer bg-white text-center small text-muted border-0">
             <i class="fas fa-sync ms-1 text-success"></i> يتم التحديث لحظياً
          </div>
        </div>
      </div>

      <!-- ========================================================== -->
      <!-- Chart 3: غياب المواد (Stacked Bar + Table) - تم الإصلاح -->
      <!-- ========================================================== -->
      <div class="col-md-4">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-header bg-white fw-bold border-bottom-0 d-flex justify-content-between align-items-center">
             <span>تحليل غياب المواد</span>
             <span class="badge bg-light text-dark border" title="أحمر: غياب | أخضر: حضور">تفصيلي</span>
          </div>
          
          <div class="card-body">
            <!-- الشارت -->
            <div class="position-relative" style="height: 200px;">
              <!-- استخدام key يجبر الشارت على إعادة الرسم عند تغير البيانات -->
              <Bar 
                v-if="chartsData.subjects_absence" 
                :key="refreshKey"
                :data="chartsData.subjects_absence" 
                :options="chartOptions.stackedBar" 
              />
            </div>

            <!-- الجدول الرقمي أسفل الشارت -->
            <div class="mt-3 pt-2 border-top" v-if="chartsData.subjects_absence">
               <div class="table-responsive" style="max-height: 120px; overflow-y: auto;">
                   <table class="table table-sm text-center small mb-0 table-hover align-middle">
                       <thead class="sticky-top bg-light">
                           <tr>
                               <th>المادة</th>
                               <th class="text-danger">غياب</th>
                               <th class="text-success">حضور</th>
                               <th>نسبة الغياب</th>
                           </tr>
                       </thead>
                       <tbody>
                           <tr v-for="(label, i) in chartsData.subjects_absence.labels" :key="i">
                               <td class="fw-bold">{{ label }}</td>
                               <!-- استخدام Optional Chaining لتجنب الأخطاء -->
                               <td class="text-danger fw-bold">{{ chartsData.subjects_absence.datasets[0].data[i] }}</td>
                               <td class="text-success">{{ chartsData.subjects_absence.datasets[1].data[i] }}</td>
                               <td class="text-muted fw-bold">
                                   {{ calculatePercentage(chartsData.subjects_absence.datasets[0].data[i], chartsData.subjects_absence.datasets[1].data[i]) }}
                               </td>
                           </tr>
                       </tbody>
                   </table>
               </div>
            </div>
          </div>

        </div>
      </div>
      <!-- ========================================================== -->

    </div>

    <!-- 4. صف التايم لاين -->
    <div class="row mb-4">
        <div class="col-12">
            <div class="card border-0 shadow-sm">
                <div class="card-header bg-white fw-bold border-bottom-0 d-flex justify-content-between">
                    <span>مؤشر ضغط العمل (Timeline)</span>
                    <span class="badge bg-light text-dark border">على مدار الساعة</span>
                </div>
                <div class="card-body position-relative" style="height: 280px;">
                    <LineChart v-if="chartsData.timeline" :data="chartsData.timeline" :options="chartOptions.line" />
                </div>
            </div>
        </div>
    </div>

    <!-- 5. الجداول التفصيلية -->
    <div class="row g-4">
      <!-- اللجان الساخنة -->
      <div class="col-md-6">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-header bg-white fw-bold text-danger">
            <i class="fas fa-fire ms-2"></i> اللجان "الساخنة" (الأكثر إبلاغاً)
          </div>
          <div class="table-responsive">
            <table class="table table-hover align-middle mb-0 text-center">
              <thead class="table-light">
                <tr>
                  <th>رقم اللجنة</th>
                  <th>عدد البلاغات</th>
                  <th>أخطر نوع</th>
                  <th>الحالة</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(comm, i) in topCommittees" :key="i">
                  <td class="fw-bold">{{ comm.committee_id }}</td>
                  <td>{{ comm.count }}</td>
                  <td><span class="badge bg-secondary">{{ comm.top_type }}</span></td>
                  <td>
                    <i v-if="comm.has_unresolved" class="fas fa-exclamation-circle text-danger" title="مشاكل عالقة"></i>
                    <i v-else class="fas fa-check-circle text-success" title="مستقرة"></i>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- المقصرون -->
      <div class="col-md-6">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-header bg-white fw-bold text-dark d-flex justify-content-between">
            <span><i class="fas fa-eye-slash ms-2 text-muted"></i> لم يقرأوا التعليمات الأخيرة</span>
            <button class="btn btn-xs btn-outline-primary py-0" @click="remindAll">إرسال تنبيه</button>
          </div>
          <div class="card-body p-0">
            <div class="list-group list-group-flush">
              <div v-for="(user, i) in unreadUsers" :key="i" class="list-group-item d-flex justify-content-between align-items-center">
                <div>
                  <span class="fw-bold d-block">{{ user.name }}</span>
                  <small class="text-muted">لجنة رقم {{ user.committee_id }}</small>
                </div>
                <span class="badge bg-warning text-dark">لم يقرأ منذ {{ user.hours_passed }} ساعة</span>
              </div>
              <div v-if="unreadUsers.length === 0" class="text-center py-4 text-success">
                <i class="fas fa-check-double mb-2"></i> الجميع ملتزم بالتعليمات
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
// استيراد المكونات المطلوبة
import { 
  Chart as ChartJS, 
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale, 
  LinearScale, 
  PointElement, 
  LineElement, 
  BarElement,
  Title 
} from 'chart.js'
import { Doughnut, Line as LineChart, Pie, Bar } from 'vue-chartjs'
import api from '@/api/axios';

// تسجيل العناصر
ChartJS.register(
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale, 
  LinearScale, 
  PointElement, 
  LineElement, 
  BarElement,
  Title
)
 
export default {
  name: 'OperationsDashboard',
  components: { Doughnut, LineChart, Pie, Bar },
  data() {
    return {
      loading: false,     // تحميل الصفحة بالكامل
      isRefreshing: false, // تحميل التحديث الخلفي
      autoRefresh: true,  // تفعيل التحديث التلقائي افتراضياً
      timer: null,
      refreshKey: 0,      // مفتاح لإجبار الشارت على التحديث
      
      stats: {
        total_today: 0,
        urgent_count: 0,
        resolution_rate: 0,
        read_rate: 0,
        top_issue_type: '-'
      },
      topCommittees: [],
      unreadUsers: [],
      
      chartsData: {
        types: null,
        timeline: null,
        attendance: null,
        subjects_absence: null
      },
      
      chartOptions: {
        doughnut: { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'right' } } },
        pie: { 
            responsive: true, 
            maintainAspectRatio: false, 
            plugins: { 
                legend: { position: 'bottom' },
                tooltip: {
                    callbacks: {
                        label: function(context) {
                            let label = context.label || '';
                            let value = context.raw;
                            let total = context.chart._metasets[context.datasetIndex].total;
                            let percentage = Math.round((value / total) * 100) + '%';
                            return label + ': ' + value + ' (' + percentage + ')';
                        }
                    }
                }
            } 
        },
        line: { responsive: true, maintainAspectRatio: false, scales: { y: { beginAtZero: true, ticks: { stepSize: 1 } } } },
        // خيارات البار المكدس (Stacked)
        stackedBar: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: { 
                legend: { display: true, position: 'top', labels: { boxWidth: 10, font: { size: 10 } } } 
            },
            scales: {
              x: { 
                  stacked: true, 
                  grid: { display: false }
              },
              y: { 
                  stacked: true, 
                  beginAtZero: true,
                  grid: { color: '#f0f0f0' }
              }
            }
        }
      }
    }
  },
  computed: {
    hasActiveAlerts() {
      return this.stats.urgent_count > 0;
    }
  },
  mounted() {
    this.refreshData(); // التحميل الأول
    this.startAutoRefresh(); // بدء التحديث التلقائي
  },
  beforeDestroy() {
    this.stopAutoRefresh();
  },
  methods: {
    toggleAutoRefresh() {
      this.autoRefresh = !this.autoRefresh;
      if(this.autoRefresh) {
        this.startAutoRefresh();
      } else {
        this.stopAutoRefresh();
      }
    },
    
    startAutoRefresh() {
        if(this.timer) clearInterval(this.timer);
        this.timer = setInterval(() => {
            this.refreshData(true); // true = تحديث خلفي (بدون لودينج كبير)
        }, 15000); // تحديث كل 15 ثانية ليكون "لحظي" أكثر
    },

    stopAutoRefresh() {
        if(this.timer) clearInterval(this.timer);
        this.timer = null;
    },

    manualRefresh() {
        this.refreshData(false);
    },

    async refreshData(isBackground = false) {
      if(!isBackground) this.loading = true;
      this.isRefreshing = true;

      try {
        const res = await api.get('/admin/operations-analytics');
        const data = res.data;

        this.stats = data.stats;
        this.topCommittees = data.top_committees;
        this.unreadUsers = data.unread_users;

        // 1. تصنيف البلاغات
        this.chartsData.types = {
          labels: data.charts.types.labels,
          datasets: [{
            backgroundColor: ['#dc3545', '#198754', '#0d6efd', '#ffc107', '#6c757d'],
            data: data.charts.types.data,
            hoverOffset: 4
          }]
        };

        // 2. الحضور والغياب (Pie)
        if (data.charts.attendance) {
            this.chartsData.attendance = {
              labels: data.charts.attendance.labels, 
              datasets: [{
                backgroundColor: ['#198754', '#dc3545'], 
                data: data.charts.attendance.data,
                hoverOffset: 4
              }]
            };
        }

        // 3. غياب المواد (Stacked Bar) - هنا الإصلاح الجوهري
        if (data.charts.subjects_absence) {
            this.chartsData.subjects_absence = {
              labels: data.charts.subjects_absence.labels, // أسماء المواد
              datasets: [
                {
                  label: 'غياب',
                  backgroundColor: '#dc3545', // أحمر
                  data: data.charts.subjects_absence.absent, // مصفوفة الغياب
                  borderRadius: 4
                },
                {
                  label: 'حضور',
                  backgroundColor: '#198754', // أخضر
                  data: data.charts.subjects_absence.present, // مصفوفة الحضور
                  borderRadius: 4
                }
              ]
            };
        }
        
        // إجبار الشارت على إعادة الرسم في بعض الحالات
        this.refreshKey++;

        // 4. التايم لاين
        this.chartsData.timeline = {
          labels: data.charts.timeline.labels,
          datasets: [{
            label: 'تدفق البلاغات',
            borderColor: '#0d6efd',
            backgroundColor: 'rgba(13, 110, 253, 0.2)',
            data: data.charts.timeline.data,
            tension: 0.4,
            fill: true
          }]
        };

      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
        this.isRefreshing = false;
      }
    },
    
    // دالة حساب النسبة للجدول
    calculatePercentage(absent, present) {
        let total = absent + present;
        if(total === 0) return '0%';
        return Math.round((absent / total) * 100) + '%';
    },

    remindAll() {
      if(!confirm("هل تريد إرسال تذكير؟")) return;
      alert("تم إرسال التنبيه");
    }
  }
}
</script>

<style scoped>
.x-small { font-size: 10px; }
.fade-animation { animation: fadeIn 1s infinite alternate; }
@keyframes fadeIn { from { opacity: 0.6; } to { opacity: 1; } }

.transition-btn { transition: all 0.3s ease; }
.blink-slow { animation: blinker 2s linear infinite; }
@keyframes blinker { 50% { opacity: 0; } }
.border-end { border-right-width: 5px !important; }
</style>